import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Play, Pause, Copy, Trash2, Edit, Flag, X, Loader2, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const CAMPAIGNS_STEPS = [
    { target: '[data-testid="new-campaign-btn"]', title: 'Criar Campanha', content: 'Clique aqui para criar uma nova campanha de divulgacao. Defina o nome, mensagem e plataforma de envio.' },
    { target: '[data-testid="campaign-search"]', title: 'Buscar Campanhas', content: 'Pesquise campanhas pelo nome para encontrar rapidamente o que precisa.' },
    { target: '[data-testid="campaigns-table"]', title: 'Lista de Campanhas', content: 'Aqui ficam todas as suas campanhas. Clique em uma para editar, pausar ou duplicar.' },
];

const STATUS_MAP = {
    active: { label: 'Ativo', class: 'sv-badge-success' },
    paused: { label: 'Pausado', class: 'sv-badge-warning' },
    draft: { label: 'Rascunho', class: 'sv-badge-muted' },
    finished: { label: 'Finalizado', class: 'sv-badge-info' },
};

const CHANNELS = ['Telegram', 'Email', 'WhatsApp', 'SMS'];

export default function CampaignsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [campaigns, setCampaigns] = useState([]);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ name: '', channel: 'Telegram', message: '', target: '' });
    const [saving, setSaving] = useState(false);
    const [menuOpen, setMenuOpen] = useState(null);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchCampaigns = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filter !== 'all') params.set('status', filter);
            if (search) params.set('search', search);
            const res = await fetch(`${API}/campaigns?${params}`, { headers });
            if (res.ok) setCampaigns(await res.json());
        } catch { } finally { setLoading(false); }
    }, [filter, search]);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    useEffect(() => {
        if (searchParams.get('action') === 'nova_campanha') { openNew(); setSearchParams({}); }
    }, [searchParams]);

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const url = editingId ? `${API}/campaigns/${editingId}` : `${API}/campaigns`;
            const method = editingId ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
            if (res.ok) {
                toast.success(editingId ? 'Campanha atualizada' : 'Campanha criada');
                setShowModal(false);
                setEditingId(null);
                setForm({ name: '', channel: 'Telegram', message: '', target: '' });
                fetchCampaigns();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Erro ao salvar');
            }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const toggleStatus = async (id) => {
        const res = await fetch(`${API}/campaigns/${id}/status`, { method: 'PUT', headers });
        if (res.ok) {
            const data = await res.json();
            setCampaigns(prev => prev.map(c => c.campaign_id === id ? { ...c, status: data.status } : c));
            toast.success(`Status alterado para ${STATUS_MAP[data.status]?.label}`);
        }
    };

    const deleteCampaign = async (id) => {
        if (!window.confirm('Excluir campanha?')) return;
        const res = await fetch(`${API}/campaigns/${id}`, { method: 'DELETE', headers });
        if (res.ok) {
            setCampaigns(prev => prev.filter(c => c.campaign_id !== id));
            toast.success('Campanha excluida');
        }
    };

    const duplicateCampaign = async (id) => {
        const res = await fetch(`${API}/campaigns/${id}/duplicate`, { method: 'POST', headers });
        if (res.ok) {
            const newCamp = await res.json();
            setCampaigns(prev => [newCamp, ...prev]);
            toast.success('Campanha duplicada');
        }
    };

    const openEdit = (c) => {
        setForm({ name: c.name, channel: c.channel, message: c.message || '', target: c.target || '' });
        setEditingId(c.campaign_id);
        setShowModal(true);
        setMenuOpen(null);
    };

    const openNew = () => {
        setForm({ name: '', channel: 'Telegram', message: '', target: '' });
        setEditingId(null);
        setShowModal(true);
    };

    return (
        <div className="sv-page" data-testid="campaigns-page">
            <ContextualTour pageKey="campanhas" steps={CAMPAIGNS_STEPS} />
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Campanhas</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{campaigns.length} campanhas no total</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={openNew} data-testid="new-campaign-btn">
                    <Plus size={14} /> Nova Campanha
                </button>
            </div>

            {/* Filters */}
            <div className="sv-card-system">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                        <input type="text" placeholder="Buscar campanha..." value={search} onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm"
                            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                            data-testid="campaign-search" />
                    </div>
                    <div className="flex gap-2">
                        {['all', 'active', 'paused', 'draft'].map(f => (
                            <button key={f} onClick={() => setFilter(f)} className={`sv-subnav-chip ${filter === f ? 'active' : ''}`}>
                                {f === 'all' ? 'Todos' : STATUS_MAP[f]?.label || f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="sv-card-system" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div className="sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
                ) : campaigns.length === 0 ? (
                    <div className="sv-empty-state">
                        <Flag />
                        <p className="sv-empty-state-title">{search || filter !== 'all' ? 'Nenhuma campanha encontrada' : 'Nenhuma campanha ainda'}</p>
                        <p className="sv-empty-state-desc">Crie sua primeira campanha para comecar a enviar ofertas</p>
                        <button className="sv-btn-gold-system sv-btn-system mt-3" onClick={openNew}>
                            <Plus size={14} /> Criar Campanha
                        </button>
                    </div>
                ) : (
                    <table className="sv-data-table" data-testid="campaigns-table">
                        <thead>
                            <tr>
                                <th>Campanha</th>
                                <th>Status</th>
                                <th>Canal</th>
                                <th>Enviados</th>
                                <th>Cliques</th>
                                <th>Conversao</th>
                                <th style={{ width: 80 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((c) => (
                                <tr key={c.campaign_id}>
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                                                <Flag size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm" style={{ color: 'var(--text)' }}>{c.name}</p>
                                                <p className="text-xs" style={{ color: 'var(--muted-2)' }}>
                                                    {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : ''}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className={`sv-badge ${STATUS_MAP[c.status]?.class}`}>{STATUS_MAP[c.status]?.label}</span></td>
                                    <td style={{ color: 'var(--muted)' }}>{c.channel}</td>
                                    <td style={{ fontWeight: 600 }}>{(c.sent || 0).toLocaleString('pt-BR')}</td>
                                    <td style={{ fontWeight: 600 }}>{(c.clicks || 0).toLocaleString('pt-BR')}</td>
                                    <td style={{ color: 'var(--sv-gold-primary)', fontWeight: 700 }}>{c.conversion || '0.0%'}</td>
                                    <td>
                                        <div className="flex gap-1 relative">
                                            <button className="sv-btn-system" style={{ padding: '4px 8px' }} onClick={() => toggleStatus(c.campaign_id)}
                                                data-testid={`toggle-${c.campaign_id}`}>
                                                {c.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                                            </button>
                                            <button className="sv-btn-system" style={{ padding: '4px 8px' }}
                                                onClick={() => setMenuOpen(menuOpen === c.campaign_id ? null : c.campaign_id)}>
                                                <MoreHorizontal size={12} />
                                            </button>
                                            {menuOpen === c.campaign_id && (
                                                <div className="absolute right-0 top-full mt-1 z-50 py-1 rounded-lg min-w-[140px]"
                                                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                                                    <button className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[var(--surface-2)]"
                                                        style={{ color: 'var(--text)' }} onClick={() => openEdit(c)}>
                                                        <Edit size={12} /> Editar
                                                    </button>
                                                    <button className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[var(--surface-2)]"
                                                        style={{ color: 'var(--text)' }} onClick={() => { duplicateCampaign(c.campaign_id); setMenuOpen(null); }}>
                                                        <Copy size={12} /> Duplicar
                                                    </button>
                                                    <button className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[var(--surface-2)]"
                                                        style={{ color: '#ef4444' }} onClick={() => { deleteCampaign(c.campaign_id); setMenuOpen(null); }}>
                                                        <Trash2 size={12} /> Excluir
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="campaign-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                                {editingId ? 'Editar Campanha' : 'Nova Campanha'}
                            </h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="Ex: Black Friday 2026" className="w-full px-3 py-2.5 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="campaign-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Canal</label>
                                <div className="flex gap-2">
                                    {CHANNELS.map(ch => (
                                        <button key={ch} className={`sv-subnav-chip ${form.channel === ch ? 'active' : ''}`}
                                            onClick={() => setForm({ ...form, channel: ch })}>{ch}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Mensagem</label>
                                <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                                    rows={3} placeholder="Texto da campanha..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="campaign-message-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Publico-alvo</label>
                                <input type="text" value={form.target} onChange={e => setForm({ ...form, target: e.target.value })}
                                    placeholder="Ex: Todos os usuarios, Canal Premium"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm"
                                    style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                    data-testid="campaign-target-input" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="campaign-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                                {editingId ? 'Salvar' : 'Criar Campanha'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
